import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  MenuItem,
  InputLabel,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';

import { validatePhoneNumber } from '../../../utils/validate';

// COMPONENTS
import CountrySelect from '../../common/inputs/CountrySelect';

// I18N
import CustomersFormattedMessages from '../../../messages/admin/CustomersFormattedMessages';

// NETWORKING
import useCustomerRequest from '../../../requests/CustomerRequest';
import AuthorisedContactForm from '../AuthorisedContactForm';

const CustomerAdd = ({ provider, backButtonClick, errorHandler }) => {
  const [edits, setEdits] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [tariff_plans, setTariffPlans] = useState([]);
  const [class_types, setClassTypes] = useState([]);
  const [error, setError] = useState(null);
  const [validated, setValidated] = useState(false);
  const { getCustomerExtras, createNewCustomer } = useCustomerRequest();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await getCustomerExtras(errorHandler);
        setTariffPlans(results.tariff_plans);
        setClassTypes(results.class_types);
      } catch (err) {
        errorHandler(err);
      }
    };

    fetchData();
  }, [errorHandler]);

  const handleFormChange = (name, value) => {
    if (name === 'code_type') {
      setEdits((prevEdits) => ({
        ...prevEdits,
        customer_type: value.charAt(0),
        customer_class: value.charAt(1),
      }));
    } else {
      setEdits((prevEdits) => ({
        ...prevEdits,
        [name]: value,
      }));
    }

    setValidated(false);
  };

  const checkEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!event.target.checkValidity()) {
      setValidated(true);
      return false;
    }

    setValidated(true);

    let customerData = edits;

    if (!customerData.customer_name) {
      setError('Missing Business Name');
      return false;
    }
    if (!customerData.contact_tn) {
      setError('Missing Contact Phone');
      return false;
    }
    if (!validatePhoneNumber(customerData.contact_tn)) {
      setError(
        'Incorrect contact phone format, should only contain numbers and "+", eg: +0123456789/0123456789'
      );
      return false;
    }
    if (!customerData.country_id) {
      setError('Missing Contact Country');
      return false;
    }
    if (!customerData.contact_name) {
      setError('Missing Administrator Name');
      return false;
    }
    if (!customerData.contact_email) {
      setError('Missing Administrator Email');
      return false;
    }
    if (!checkEmail(customerData.contact_email)) {
      setError('The email provided is not in a valid format.');
      return false;
    }

    if (!customerData.tariff_plan_id && tariff_plans.length > 0) {
      customerData.tariff_plan_id = tariff_plans[0].tariff_plan_id;
    }
    if (!customerData.customer_type && class_types.length > 0) {
      customerData.customer_type = class_types[0].code_type;
    }
    if (!customerData.customer_class && class_types.length > 0) {
      customerData.customer_class = class_types[0].code_class;
    }

    setIsSaving(true);

    try {
      const newObject = await createNewCustomer(customerData, errorHandler);
      if (typeof newObject.error === 'undefined') {
        backButtonClick('refresh');
      }
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsSaving(false);
    }
  };

  const generateTariffPlanSelect = () => {
    if (!tariff_plans || typeof tariff_plans === 'undefined') return null;

    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }}>
          {CustomersFormattedMessages.tariffPlan}
        </InputLabel>
        <TextField
          select
          name="tariff_plan_id"
          fullWidth
          value={
            edits.tariff_plan_id ||
            (tariff_plans.length > 0 ? tariff_plans[0].tariff_plan_id : '')
          }
          onChange={(e) => handleFormChange('tariff_plan_id', e.target.value)}
          size="small"
        >
          {tariff_plans.map((plan) => (
            <MenuItem key={plan.tariff_plan_id} value={plan.tariff_plan_id}>
              {plan.select_name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    );
  };

  const generateClassTypeSelect = () => {
    if (!class_types || typeof class_types === 'undefined') return null;

    return (
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          alignItems: { md: 'center' },
          gap: { md: 2 },
        }}
      >
        <InputLabel sx={{ minWidth: 200 }}>
          {CustomersFormattedMessages.typeClass}
        </InputLabel>
        <TextField
          select
          name="code_type"
          value={
            edits.customer_type && edits.customer_class
              ? `${edits.customer_type}${edits.customer_class}`
              : class_types.length > 0
              ? class_types[0].code_type + class_types[0].code_class
              : ''
          }
          fullWidth
          size="small"
          onChange={(e) => handleFormChange('code_type', e.target.value)}
        >
          {class_types.map((type) => (
            <MenuItem
              key={type.class_type_status_id}
              value={type.code_type + type.code_class}
            >
              {type.select_name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    );
  };

  const generateCustomerFormBody = () => {
    return (
      <Box marginBottom={4}>
        <Typography variant="h2" gutterBottom>
          {CustomersFormattedMessages.businessInformation}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }} required>
                {CustomersFormattedMessages.businessName}
              </InputLabel>
              <TextField
                name="customer_name"
                value={edits.customer_name || ''}
                onChange={(e) =>
                  handleFormChange('customer_name', e.target.value)
                }
                required
                fullWidth
                size="small"
                error={validated && !edits.customer_name}
                helperText={
                  validated && !edits.customer_name
                    ? 'Please provide a business name.'
                    : ''
                }
              />
            </Stack>
          </Grid>
          <Grid size={12}>{generateTariffPlanSelect()}</Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.customerABN}
              </InputLabel>
              <TextField
                name="customer_abn"
                value={edits.customer_abn || ''}
                onChange={(e) =>
                  handleFormChange('customer_abn', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.customerReferenceNo}
              </InputLabel>

              <TextField
                name="customer_ref_no"
                value={edits.customer_ref_no || ''}
                onChange={(e) =>
                  handleFormChange('customer_ref_no', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>
          <Grid size={12}>{generateClassTypeSelect()}</Grid>
        </Grid>
      </Box>
    );
  };

  const generateContactFormBody = () => {
    return (
      <Box marginBottom={4}>
        <Typography variant="h2" gutterBottom>
          {CustomersFormattedMessages.contactInformation}
        </Typography>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }} required>
                {CustomersFormattedMessages.phone}
              </InputLabel>

              <TextField
                name="contact_tn"
                value={edits.contact_tn || ''}
                onChange={(e) => handleFormChange('contact_tn', e.target.value)}
                required
                fullWidth
                size="small"
                error={validated && !edits.contact_tn}
                helperText={
                  validated && !edits.contact_tn
                    ? 'Please provide a contact phone number.'
                    : ''
                }
              />
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.fax}
              </InputLabel>

              <TextField
                name="contact_fax"
                value={edits.contact_fax || ''}
                onChange={(e) =>
                  handleFormChange('contact_fax', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.streetAddress}
              </InputLabel>

              <TextField
                name="contact_address1"
                value={edits.contact_address1 || ''}
                onChange={(e) =>
                  handleFormChange('contact_address1', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.continued}
              </InputLabel>

              <TextField
                name="contact_address2"
                value={edits.contact_address2 || ''}
                onChange={(e) =>
                  handleFormChange('contact_address2', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.city}
              </InputLabel>

              <TextField
                name="contact_city"
                value={edits.contact_city || ''}
                onChange={(e) =>
                  handleFormChange('contact_city', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.state}
              </InputLabel>

              <TextField
                name="contact_state"
                value={edits.contact_state || ''}
                onChange={(e) =>
                  handleFormChange('contact_state', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }}>
                {CustomersFormattedMessages.postcode}
              </InputLabel>

              <TextField
                name="contact_postcode"
                value={edits.contact_postcode || ''}
                onChange={(e) =>
                  handleFormChange('contact_postcode', e.target.value)
                }
                fullWidth
                size="small"
              />
            </Stack>
          </Grid>

          <Grid size={12}>
            <Stack
              sx={{
                flexDirection: { md: 'row' },
                alignItems: { md: 'center' },
                gap: { md: 2 },
              }}
            >
              <InputLabel sx={{ minWidth: 200 }} required>
                {CustomersFormattedMessages.country}
              </InputLabel>

              <CountrySelect
                name="country_id"
                value={edits.country_id}
                onChange={(_, newValue) =>
                  handleFormChange('country_id', newValue)
                }
                required
                fullWidth
                selectDefault
                error={validated && !edits.country_id}
                helperText={
                  validated && !edits.country_id
                    ? 'Please select a country.'
                    : null
                }
                errorHandler={errorHandler}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const generateError = () => {
    if (!error) return null;
    return <Alert severity="error">{error}</Alert>;
  };

  const generateFormButton = () => {
    if (isSaving) {
      return <CircularProgress />;
    }

    return (
      <Button variant="contained" color="secondary" type="submit">
        Create Customer
      </Button>
    );
  };

  const generateBody = () => {
    return (
      <Box
        component="form"
        onSubmit={handleSubmit}
        onChange={(e) => handleFormChange(e.target.name, e.target.value)}
        maxWidth={800}
      >
        {generateCustomerFormBody()}
        {generateContactFormBody()}

        <AuthorisedContactForm />

        <Stack gap={2}>
          <Typography fontSize="small">
            <Typography component="span" sx={{ color: 'secondary.light' }}>
              *
            </Typography>{' '}
            {CustomersFormattedMessages.requiredField}
          </Typography>

          {generateError()}

          <Box>{generateFormButton()}</Box>
        </Stack>
      </Box>
    );
  };

  return <>{generateBody()}</>;
};

export default CustomerAdd;
